import { useCallback, useEffect } from 'react';
import { VIMEO_OEMBED_API } from '../../constants';
import Player from '@vimeo/player';

export const useVimeoPlayer = ({
  url,
  autoPlay,
  loop,
  enabledControls,
  enabledTitle,
  startTime,
  endTime,
  ref,
}: {
  url?: string;
  autoPlay?: boolean;
  loop?: boolean;
  enabledControls?: boolean;
  enabledTitle?: boolean;
  startTime?: number;
  endTime?: number;
  ref?: React.RefObject<HTMLDivElement>;
}) => {
  const setAspectRatio = useCallback(async (): Promise<void> => {
    if (!url || !ref || !ref.current) {
      return;
    }
    const setStyles = (width: number, height: number) => {
      const aspectRatio = width / height;
      ref.current?.style.setProperty('--width', '100%');
      ref.current?.style.setProperty('--height', 'auto');

      if (aspectRatio > 1) {
        ref.current?.style.setProperty('--aspect-ratio', '16 / 9');
      } else if (aspectRatio === 1) {
        ref.current?.style.setProperty('--aspect-ratio', '1 / 1');
      } else {
        ref.current?.style.setProperty('--aspect-ratio', '9 / 16');
      }
    };
    try {
      const oembedUrl = `${VIMEO_OEMBED_API}?url=${encodeURIComponent(url)}`;
      const response = await fetch(oembedUrl);

      if (!response.ok) {
        return;
      }

      const videoData = await response.json();

      ref.current?.style.setProperty('--visibility', 'hidden');

      if (videoData.width && videoData.height) {
        setStyles(videoData.width, videoData.height);
      }
    } catch (error) {
      setStyles(100, 100);
    }
  }, [ref, url]);

  useEffect(() => {
    if (!url || !ref || !ref.current) {
      return;
    }

    let player: Player | undefined;

    const initializePlayer = async (): Promise<void> => {
      await setAspectRatio();

      if (!ref.current) {
        return;
      }

      player = new Player(ref.current, {
        url: url!,
        autoplay: autoPlay,
        loop,
        muted: autoPlay,
        controls: enabledControls,
        title: enabledTitle,
        /*
            player supports more parameters, but types are incomplete
            check for complete api here:
            https://developer.vimeo.com/player/sdk/embed
        */
        // @ts-expect-error
        start_time: startTime,
        end_time: endTime,
      });

      await player.ready();
      ref.current?.style.setProperty('--visibility', 'visible');
    };

    void initializePlayer();

    return () => {
      void player?.destroy();
    };
  }, [
    url,
    loop,
    autoPlay,
    enabledControls,
    enabledTitle,
    startTime,
    endTime,
    setAspectRatio,
    ref,
  ]);
};
